import React, { useCallback, useState } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import CustomField from "@mapmycustomers/shared/types/customField/CustomField";
import { EntityTypeSupportingCustomFields } from "@mapmycustomers/shared/types/entity";
import { Modal, TextField } from "@mapmycustomers/ui";

import Checkbox from "@app/component/input/Checkbox";
import { updateSchema } from "@app/scene/settings/component/ManageFields/store/actions";
import { isCustomFieldDeleting } from "@app/store/customFields";
import { deleteCustomField } from "@app/store/customFields/actions";
import { RootState } from "@app/store/rootReducer";
import { AnalyticsService } from "@app/util/analytic/AnalyticsService";

import styles from "./ConfirmFieldDeletionModal.module.scss";

interface Props {
  analyticIssuer?: AnalyticsService;
  customFieldId: CustomField["id"];
  deleteCustomField: typeof deleteCustomField.request;
  deletionInProgress: boolean;
  entityType: EntityTypeSupportingCustomFields;
  onHide: () => void;
  updateSchema: typeof updateSchema.request;
}

const ConfirmFieldDeletionModal: React.FC<Props> = ({
  analyticIssuer,
  customFieldId,
  deleteCustomField,
  deletionInProgress,
  entityType,
  onHide,
  updateSchema,
}) => {
  const intl = useIntl();
  const [deleteText, setDeleteText] = useState<string>("");
  const [deleteTextError, setDeleteTextError] = useState<string | undefined>();
  const [sendReport, setSendReport] = useState<boolean>(false);

  const handleHide = useCallback(() => {
    updateSchema();
    onHide();
  }, [onHide, updateSchema]);

  const handleConfirm = useCallback(() => {
    analyticIssuer?.clicked(["Trash Can Icon", "Delete Custom Field"]);
    if (deleteText.trim().toLowerCase() !== "delete") {
      setDeleteTextError(
        intl.formatMessage({
          id: "settings.customFields.deleteFieldModal.deleteTextError",
          defaultMessage: "Text must match exactly",
          description: "Delete text error  for Confirm field deletion modal",
        })
      );
    } else {
      setDeleteTextError(undefined);
      deleteCustomField({ id: customFieldId, callback: handleHide, entityType, sendReport });
    }
  }, [
    analyticIssuer,
    customFieldId,
    deleteCustomField,
    deleteText,
    entityType,
    intl,
    handleHide,
    sendReport,
    setDeleteTextError,
  ]);

  return (
    <Modal
      cancelButtonProps={{
        disabled: deletionInProgress,
      }}
      closable={!deletionInProgress}
      okButtonProps={{
        danger: true,
        disabled: deletionInProgress,
        loading: deletionInProgress,
        type: "default",
      }}
      okText={intl.formatMessage({
        id: "settings.customFields.deleteFieldModal.ok",
        defaultMessage: "Delete Custom Field",
        description: "Ok text for Confirm field deletion modal",
      })}
      onCancel={onHide}
      onOk={handleConfirm}
      title={
        <span className={styles.modalTitle}>
          {intl.formatMessage({
            id: "settings.customFields.deleteFieldModal.title",
            defaultMessage: "Confirm field deletion",
            description: "Title for Confirm field deletion modal",
          })}
        </span>
      }
      visible
    >
      <div className={styles.container}>
        <div className={styles.paragraph1}>
          {intl.formatMessage({
            id: "settings.customFields.deleteFieldModal.paragraph1",
            defaultMessage:
              "This action will permanently delete this custom field and it’s data. Deletion cannot be reversed.",
            description: "Paragraph 1 for Confirm field deletion modal",
          })}
        </div>
        <div>
          {intl.formatMessage({
            id: "settings.customFields.deleteFieldModal.paragraph2",
            defaultMessage: "This action will delete",
            description: "Paragraph 2 for Confirm field deletion modal",
          })}
          <ul>
            <li>
              {intl.formatMessage({
                id: "settings.customFields.deleteFieldModal.paragraph2.point1",
                defaultMessage: "The custom field configuration",
                description: "Paragraph 2 - point 1 for Confirm field deletion modal",
              })}
            </li>
            <li>
              {intl.formatMessage({
                id: "settings.customFields.deleteFieldModal.paragraph2.point2",
                defaultMessage: "Any values logged for the given field",
                description: "Paragraph 2 - point 2 for Confirm field deletion modal",
              })}
            </li>
          </ul>
        </div>
        <div>
          <Checkbox checked={sendReport} onChange={setSendReport}>
            {intl.formatMessage({
              id: "settings.customFields.deleteFieldModal.sendReport",
              defaultMessage:
                "Send all data from this field to my email upon deletion (recommended)",
              description: "Send report - for Confirm field deletion modal",
            })}
          </Checkbox>
        </div>
        <div className={styles.paragraph4}>
          <TextField
            className={styles.nameField}
            error={deleteTextError}
            label={intl.formatMessage(
              {
                id: "settings.customFields.deleteFieldModal.paragraph4",
                defaultMessage: "To delete, type <b>DELETE</b> to confirm",
                description: "Paragraph 4 - point 2 for Confirm field deletion modal",
              },
              { b: (text) => <b>{text}</b> }
            )}
            onChange={setDeleteText}
            value={deleteText}
          />
        </div>
      </div>
    </Modal>
  );
};

const mapStateToProps = (state: RootState) => ({
  deletionInProgress: isCustomFieldDeleting(state),
});

const mapDispatchToProps = {
  deleteCustomField: deleteCustomField.request,
  updateSchema: updateSchema.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(ConfirmFieldDeletionModal);
